import { HamiltonProductReduced } from '../../api/products-api';
import { addItemToShoppingCart } from '../../api/shopping-cart-api';
import { currentBreakpoint } from '../../utils/resize.utils';
import { toastNotification } from '../toast-notification/toast-notification.module';
import { shoppingCart } from '../shopping-cart/shopping-cart.module';
import { wishListData } from '../../wish-list-data.module';
import { getAllWishlists, addItemToWishList, deleteItemFromWishList } from '../../api/wish-list-api';
import { wishList } from '../wish-list/wish-list.module';
import { eCatalog } from './e-catalog.module';

/**
 * Function to add the given product to the shopping cart.
 * @param product The product to add
 * @param labelAddToShoppingCart Label for "add to shopping cart"
 * @param labelGoToShoppingCart Label for "go to shopping cart"
 * @param errorMessage Message to display if an error occurs.
 */
export const addToShoppingCart = (
  product: HamiltonProductReduced,
  labelAddToShoppingCart: string,
  labelGoToShoppingCart: string,
  errorMessage: string
): void => {
  // add selected item to the shopping cart
  addItemToShoppingCart(product.nodeid).then((success) => {
    if (success) {
      const breakpoint = currentBreakpoint();
      if (breakpoint.isMobilePortrait || breakpoint.isMobileLandscape) {
        // on mobile show a toast
        toastNotification.createMobileToast(product, labelAddToShoppingCart, labelGoToShoppingCart, () => {
          shoppingCart.openShoppingCart();
        });
      } else {
        // Show toastify toast on desktop
        toastNotification.createDesktopToast(product, labelAddToShoppingCart);
        // reload the shopping cart if it is open
        shoppingCart.reloadShoppingCart();
      }
    } else {
      toastNotification.createErrorToastMessage(errorMessage);
    }
  });
};

/**
 * Listen for clicks on the pin button (tile or product detail) and directly add to default wishlist if set.
 * @param pinButton The wishlist button element
 * @param productId The product ID
 */
export const addListenerForWishlistPinButton = (pinButton: HTMLElement, productId: string): void => {
  if (!pinButton) {
    return;
  }

  // Also store the ID of the product that should be added to a wish list
  // this ID will be set to the overlay and later on used in the API call if overlay opens
  pinButton.setAttribute('data-overlay-associated-id', productId);

  pinButton.addEventListener(
    'click',
    (e: MouseEvent) => {
      if (!window.localStorage) {
        return;
      }

      const userId = document.querySelector('html')?.dataset.uid || '';
      const defaultWishlist = window.localStorage.getItem('defaultWishlist' + userId);

      if (!defaultWishlist) {
        return;
      }

      const isAlreadyInWishlist = wishListData.productIdsInWishLists
        ? wishListData.productIdsInWishLists.indexOf(productId) > -1
        : pinButton.classList.contains('button--primary');

      if (isAlreadyInWishlist) {
        // Product already in a wishlist. Let default overlay behavior open so user can select/remove
        return;
      }

      // Intercept click: bypass overlay and directly add to default wishlist
      e.stopImmediatePropagation();
      e.preventDefault();

      getAllWishlists()
        .then((wishLists) => {
          const defaultWishlistObject = wishLists.filter((wishlist) => wishlist['@name'] === defaultWishlist)[0];
          if (!defaultWishlistObject) {
            window.localStorage.removeItem('defaultWishlist' + userId);
            return;
          }

          if (!defaultWishlistObject.items) {
            defaultWishlistObject.items = [];
          }

          const defaultWishlistItems = defaultWishlistObject.items.filter((item) => item['@id'] === productId);
          const currentItemIds = defaultWishlistObject.items?.map((item) => item['@id']) || [];
          const overlay = document.querySelector<HTMLElement>('.overlay-wrapper[data-label-wishlist-added]');
          const labelWishlistAdded = overlay?.dataset.labelWishlistAdded || '';
          const labelWishlistRemoved = overlay?.dataset.labelWishlistRemoved || '';
          const errorMessage = overlay?.dataset.errorMessage || '';
          const labelWishlistGoTo = overlay?.dataset.labelWishlistGoTo || '';
          let added = false;

          if (defaultWishlistItems.length === 0) {
            added = true;
            addItemToWishList(productId, currentItemIds, defaultWishlist)
              .then(() => {
                if (!wishListData.productIdsInWishLists) {
                  wishListData.productIdsInWishLists = [productId];
                } else if (!wishListData.productIdsInWishLists.includes(productId)) {
                  wishListData.productIdsInWishLists.push(productId);
                }
                eCatalog.eCatalogProducts?.updatePinButtonStates();
                eCatalog.eCatalogDetail?.updatePinAndShoppingCartButtonState();
              })
              .catch((err) => {
                console.error(err);
                toastNotification.createErrorToastMessage(errorMessage);
              });
          } else {
            deleteItemFromWishList(productId, currentItemIds, defaultWishlist)
              .then(() => {
                wishListData.removeProductFromCachedWishList(productId);
                eCatalog.eCatalogProducts?.updatePinButtonStates();
                eCatalog.eCatalogDetail?.updatePinAndShoppingCartButtonState();
              })
              .catch((err) => toastNotification.createErrorToastMessage(errorMessage));
          }

          const labelText = added ? labelWishlistAdded : labelWishlistRemoved;
          const breakpoint = currentBreakpoint();
          let product = document.querySelector(`[data-product-id="${productId}"]`);
          const fallbackIcons = product
            ? product.getAttribute('data-fallback-icons')
            : document
                .querySelector<HTMLImageElement>('.text-and-image-component__image-wrapper')
                ?.getAttribute('data-fallback-icons');
          const productName = product
            ? product.querySelector<HTMLElement>('.tile__headline')?.innerText || ''
            : document.querySelector<HTMLElement>('.product-detail__headline')?.innerText || '';
          const partNumber = product
            ? product.querySelector<HTMLElement>('.tile__subtitle')?.innerText || ''
            : document.querySelector<HTMLElement>('.product-detail__id')?.innerText || '';
          const imageSmall = product
            ? product.querySelector<HTMLImageElement>('img')?.src || ''
            : document.querySelector<HTMLImageElement>('.text-and-image-component__image-wrapper img')?.src || '';

          const fallbackIconArray = fallbackIcons ? JSON.parse(fallbackIcons) : null;
          const productInfo = { productName, partNumber, imageSmall, nodeid: productId, icons: fallbackIconArray };

          if (breakpoint.isMobilePortrait || breakpoint.isMobileLandscape) {
            toastNotification.createMobileToast(productInfo, labelText, labelWishlistGoTo, () => {
              wishList.openWishList();
            });
          } else {
            toastNotification.createDesktopToast(productInfo, labelText);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    true
  );
};

